import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import AppShell from '../../components/layout/AppShell';
import { useLang } from '../../contexts/LangContext';
import { authAPI } from '../../utils/api';

const Q = (s) => `"${s}"`;
const APOS = "\u2019";

const TermsOfService = () => {
  const navigate = useNavigate();
  const { lang } = useLang();
  const [user, setUser] = useState(null);
  useEffect(() => { authAPI.getMe().then((r) => setUser(r.data)).catch(() => {}); }, []);
  const today = new Date().toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US');

  return (
    <AppShell user={user} title={lang === 'tr' ? 'Kullanım Koşulları' : 'Terms of Service'}>
      <div className="max-w-3xl mx-auto p-4 sm:p-8 md:p-12 space-y-6 text-stone-800">
        <button type="button" onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-sm text-stone-600 hover:text-stone-900" data-testid="legal-back">
          <ArrowLeft className="w-4 h-4" /> {lang === 'tr' ? 'Geri' : 'Back'}
        </button>

        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 text-teal-800 text-xs font-semibold uppercase tracking-wider">
            <FileText className="w-3 h-3" /> {lang === 'tr' ? 'Sözleşme' : 'Agreement'}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {lang === 'tr' ? 'Kullanım Koşulları' : 'Terms of Service'}
          </h1>
          <p className="text-sm text-stone-500">{lang === 'tr' ? 'Son güncelleme' : 'Last updated'}: {today}</p>
        </header>

        {lang === 'tr' ? (
          <article className="prose prose-stone max-w-none leading-relaxed space-y-5">
            <p>
              Hafızım uygulamasını {Q('Hizmet')} kullanarak aşağıdaki koşulları kabul etmiş olursunuz.
              Bu sözleşmeyi kabul etmiyorsanız lütfen Hizmeti kullanmayınız.
            </p>

            <h2 className="text-xl font-bold mt-6">1. Hizmetin Niteliği</h2>
            <p>
              Hafızım, Kur{APOS}an-ı Kerim{APOS}i okumaya, dinlemeye, ezberlemeye ve İslami eğitime yardımcı olmak amacıyla geliştirilmiş
              ücretsiz bir öğretim aracıdır. Sunulan içerikler eğitim ve öğretim niteliğindedir; herhangi bir fetva, hukuki tavsiye
              veya resmi diyanet hizmeti niteliği taşımaz.
            </p>

            <h2 className="text-xl font-bold mt-6">2. Hesap Sorumluluğu</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Hesabınızın güvenliğinden ve şifrenizden yalnızca siz sorumlusunuz.</li>
              <li>Hesabınız üzerinden gerçekleştirilen tüm işlemlerden sorumlu olduğunuzu kabul edersiniz.</li>
              <li>Yetkisiz erişim tespit ederseniz derhal bizimle iletişime geçmelisiniz.</li>
            </ul>

            <h2 className="text-xl font-bold mt-6">3. Kabul Edilmeyen Kullanımlar</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li>Hizmete zarar verecek veya çalışmasını engelleyecek girişimler</li>
              <li>Diğer kullanıcıları taciz, tehdit veya rahatsız edici içerik paylaşımı (sohbet, profil, grup hatim adlandırmaları dahil)</li>
              <li>Kur{APOS}an-ı Kerim{APOS}e veya dini değerlere saygısızlık içeren paylaşım</li>
              <li>Telif haklarını ihlal eden içerik yükleme</li>
              <li>Otomatik araçlarla kötüye kullanım (bot, scraper vb.)</li>
            </ul>
            <p>Yukarıdaki ihlaller halinde hesap, önceden bildirim yapılmaksızın askıya alınabilir veya silinebilir.</p>

            <h2 className="text-xl font-bold mt-6">4. Fikri Mülkiyet</h2>
            <p>
              Uygulamada kullanılan Kur{APOS}an-ı Kerim metni ve sesli okumalar üçüncü taraf kaynaklara (örn. alquran.cloud, mp3quran.net,
              Diyanet İşleri Başkanlığı{APOS}nın açık kaynak içerikleri) aittir ve telif hakları kendi sahiplerine aittir. Uygulama
              arayüzü, kod ve ayet eşleştirme yapısı Hafızım{APOS}a aittir. İzinsiz çoğaltma yasaktır.
            </p>

            <h2 className="text-xl font-bold mt-6">5. Hizmetin Sürekliliği</h2>
            <p>
              Hizmet {Q('olduğu gibi')} sunulur. Kesintisiz, hatasız veya tamamen güvenli bir hizmet garanti edilmez.
              Hizmetin geçici veya kalıcı olarak durdurulması, içeriğin değiştirilmesi veya yeni özellikler eklenmesi haklarımız saklıdır.
            </p>

            <h2 className="text-xl font-bold mt-6">6. Sorumluluk Sınırı</h2>
            <p>
              Yasaların izin verdiği azami ölçüde, Hafızım; doğrudan, dolaylı, arızi veya sonuç olarak ortaya çıkan zararlardan
              (veri kaybı, kâr kaybı dahil) sorumlu tutulamaz. Bu sınır KVKK ve tüketici mevzuatından doğan haklarınızı etkilemez.
            </p>

            <h2 className="text-xl font-bold mt-6">7. Çocukların Kullanımı</h2>
            <p>
              13 yaşın altındaki kullanıcılar ebeveyn veya yasal vasi gözetiminde kullanmalıdır. Çocuk Modu ebeveyn rızasına dayanır.
              Çocuğunuz adına hesap açıyorsanız, bu sözleşmeyi onun adına kabul ettiğinizi beyan etmiş olursunuz.
            </p>

            <h2 className="text-xl font-bold mt-6">8. Hesap Kapatma</h2>
            <p>
              Hesabınızı dilediğiniz zaman <strong>Ayarlar &gt; Hesabımı Sil</strong> bölümünden veya
              <a href="mailto:iletisim@hafizim.com" className="text-teal-700 underline"> iletisim@hafizim.com</a> adresine başvurarak kapatabilirsiniz.
              Hesap kapatıldığında verileriniz Gizlilik Politikasında belirtilen süreler içinde silinir.
            </p>

            <h2 className="text-xl font-bold mt-6">9. Değişiklikler</h2>
            <p>Koşullar değişebilir; önemli değişikliklerde uygulama içinde bildirim yapılır. Kullanıma devam ederseniz değişiklikleri kabul etmiş sayılırsınız.</p>

            <h2 className="text-xl font-bold mt-6">10. Uygulanacak Hukuk</h2>
            <p>İşbu sözleşme Türkiye Cumhuriyeti yasalarına tabidir. Doğacak uyuşmazlıklarda İstanbul mahkemeleri ve icra daireleri yetkilidir.</p>

            <h2 className="text-xl font-bold mt-6">11. İletişim</h2>
            <p><a href="mailto:iletisim@hafizim.com" className="text-teal-700 underline">iletisim@hafizim.com</a></p>
          </article>
        ) : (
          <article className="prose prose-stone max-w-none leading-relaxed space-y-5">
            <p>By using Hafızım you agree to these Terms.</p>
            <h2 className="text-xl font-bold mt-6">1. Service nature</h2>
            <p>Educational Quran memorization tool. Not a substitute for official religious authority.</p>
            <h2 className="text-xl font-bold mt-6">2. Acceptable use</h2>
            <p>No harassment, no copyright violation, no scraping/bots, no disrespect of religious content. Violations may lead to account suspension.</p>
            <h2 className="text-xl font-bold mt-6">3. Intellectual property</h2>
            <p>Quran text and audio belong to their respective sources. App code and UI belong to Hafızım.</p>
            <h2 className="text-xl font-bold mt-6">4. Liability</h2>
            <p>Service provided {Q('as is')}. No warranty of uninterrupted operation.</p>
            <h2 className="text-xl font-bold mt-6">5. Children</h2>
            <p>Users under 13 must use under parental supervision.</p>
            <h2 className="text-xl font-bold mt-6">6. Account termination</h2>
            <p>You may delete your account anytime via Settings or by emailing iletisim@hafizim.com.</p>
            <h2 className="text-xl font-bold mt-6">7. Governing law</h2>
            <p>Republic of Turkey law applies.</p>
          </article>
        )}
      </div>
    </AppShell>
  );
};

export default TermsOfService;
